import type { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { appErrorOf, SESSION_EXPIRED_EVENT, STATUS_OF } from "@engineernow/convex-client";
import { getEngineerNowClient } from "./client";

// Вызов функций Convex из сервисов админки. Ошибки — в форме ошибки axios
// ({response: {status, data: {message}}, message}), как читают страницы. 401/403 — как у
// прежнего interceptor после неудачного /admin/auth/refresh: выход (событие сессии).

export class HttpLikeError extends Error {
  response: { status: number; data: { message?: string; [key: string]: unknown } };
  constructor(status: number, message: string | undefined, data: Record<string, unknown> = {}) {
    super(message ?? `Request failed with status code ${status}`);
    this.response = { status, data: { ...data, message } };
  }
}

function toHttpError(error: unknown): HttpLikeError {
  if (error instanceof HttpLikeError) return error;
  const app = appErrorOf(error);
  if (app) return new HttpLikeError(STATUS_OF[app.code] ?? 400, app.message, { code: app.code, ...(app.details ?? {}) });
  return new HttpLikeError(500, undefined);
}

type Kind = "query" | "mutation" | "action";

async function call<K extends Kind, F extends FunctionReference<K>>(kind: K, ref: F, args: FunctionArgs<F>): Promise<FunctionReturnType<F>> {
  const { convex } = getEngineerNowClient();
  try {
    if (kind === "query") return (await convex.query(ref as FunctionReference<"query">, args)) as FunctionReturnType<F>;
    if (kind === "mutation") return (await convex.mutation(ref as FunctionReference<"mutation">, args)) as FunctionReturnType<F>;
    return (await convex.action(ref as FunctionReference<"action">, args)) as FunctionReturnType<F>;
  } catch (error) {
    const httpError = toHttpError(error);
    const code = httpError.response.data.code;
    if (code === "UNAUTHORIZED" || code === "FORBIDDEN") window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw httpError;
  }
}

export const convexQuery = <F extends FunctionReference<"query">>(ref: F, args: FunctionArgs<F>) => call("query", ref, args);
export const convexMutation = <F extends FunctionReference<"mutation">>(ref: F, args: FunctionArgs<F>) => call("mutation", ref, args);
export const convexAction = <F extends FunctionReference<"action">>(ref: F, args: FunctionArgs<F>) => call("action", ref, args);

// Файл → хранилище Convex; storageId передаётся функции обработки (картинки, Excel).
export async function uploadFile(file: Blob): Promise<string> {
  const { api } = await import("@engineernow/convex-client");
  const url = await convexMutation(api.uploads.generateUploadUrl, {});
  const response = await fetch(url, { method: "POST", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
  if (!response.ok) throw new HttpLikeError(response.status, undefined);
  return ((await response.json()) as { storageId: string }).storageId;
}

// Выгрузки (Excel) — action возвращает ссылку на временный файл; браузер скачивает его.
export async function downloadUrl(url: string, filename: string) {
  const response = await fetch(url);
  if (!response.ok) throw new HttpLikeError(response.status, undefined);
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
